import React, { useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Spin,
  Tabs,
  Dropdown,
  Menu
} from 'antd';
import {
  myLeadsHeaderAtom,
  reportModalAtom
} from '../../../../atoms/Atoms';
import MainPage from '../../../shared-components/main-page/MainPage';
import { H1 } from '../../../shared-components/typography/Title';
import Card from '../../../shared-components/container/Card';
import LeadsLost from './shared-components/LeadsLost';
import LeadsClosed from './shared-components/LeadsClosed';
import LeadsOpen from './shared-components/LeadsOpen';
import LeadsPending from './shared-components/LeadsPending';
import { thousand } from '../../../../functions';
import PayoutsSummary from './shared-components/PayoutsSummary';
import OrgSettersSummaryTable from './shared-components/OrgSettersSummaryTable';
import OrgClosersSummaryTable from './shared-components/OrgClosersSummaryTable';
import LeadsSearchTable from './shared-components/LeadsSearchTable';
import InstallMaps from './shared-components/InstallMaps';

function AdminLeads({ includeBack }) {
  const history = useHistory();
  const { TabPane } = Tabs;
  const { t } = useTranslation();
  const myLeadsHeader = useRecoilValue(myLeadsHeaderAtom);
  const [selectedTab, setSelectedTab] = useState('1');
  const [selectedLeadsTab, setSelectedLeadsTab] = useState(1);
  const setReportModal = useSetRecoilState(reportModalAtom);

  return (
    <MainPage includeBack={includeBack ?? true} requiresCloser>
      <div className="md:flex mt-4 space-y-2 space-x-2">
        <H1 text={t('admin_leads')} className="mr-auto" />
        <Button
          className="ml-auto w-full md:w-auto"
          type="primary"
          onClick={() => history.push('/dashboard')}
        >
          {t('dashboard')}
        </Button>
        <Dropdown
          overlay={
            <Menu mode="inline" className="p-0">
              <Menu.Item
                id="watts_sold"
                className="text-xs flex items-center"
                onClick={() => {
                  setReportModal({
                    type: 'watts_sold'
                  });
                }}
              >
                {t('watts_sold')}
              </Menu.Item>
            </Menu>
          }
        >
          <Button
            className="w-full md:w-auto"
            type="primary"
          >
            {t('reports')}
          </Button>
        </Dropdown>
        <Button
          className="ml-auto w-full md:w-auto"
          type="primary"
          onClick={() => history.push('/calendar')}
        >
          {t('calendar')}
        </Button>
      </div>
      <Tabs activeKey={selectedTab} onChange={(tab) => setSelectedTab(tab)}>
        <TabPane key="1" tab={t('opportunities')}>
          <div className="grid gap-4 mt-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-5">
            <div className="col-span-1 md:col-span-2 lg:col-span-1 ">
              <Spin spinning={myLeadsHeader?.loading}>
                <button
                  aria-label={t('all_opportunities')}
                  type="button"
                  className={`w-full border-4 rounded-sm ${selectedLeadsTab === -1 ? 'border-primaryColor' : 'border-transparent'}`}
                  onClick={() => setSelectedLeadsTab(-1)}
                >
                  <Card
                    title={t('all_opportunities')}
                    description={thousand((myLeadsHeader?.totalNotAssigned +
                                           myLeadsHeader?.totalPendingAssigned +
                                           myLeadsHeader?.totalPendingNoAppointment +
                                           myLeadsHeader?.totalAssigned +
                                           myLeadsHeader?.totalRevisit +
                                           myLeadsHeader?.totalFailedCredit +
                                           myLeadsHeader?.totalInstalled +
                                           myLeadsHeader?.totalActivated +
                                           myLeadsHeader?.totalLost))}
                    fakeChildren
                  />
                </button>
              </Spin>
            </div>
            <Spin spinning={myLeadsHeader?.loading}>
              <button
                aria-label={t('leads')}
                type="button"
                className={`col-span-1 w-full border-4 rounded-sm ${selectedLeadsTab === 0 ? 'border-primaryColor' : 'border-transparent'}`}
                onClick={() => setSelectedLeadsTab(0)}
              >
                <Card
                  title={t('leads')}
                  description={thousand((myLeadsHeader?.totalNotAssigned + myLeadsHeader?.totalPendingAssigned + myLeadsHeader?.totalPendingNoAppointment))}
                  fakeChildren
                />
              </button>
            </Spin>
            <Spin spinning={myLeadsHeader?.loading}>
              <button
                aria-label={t('deals')}
                type="button"
                className={`col-span-1 w-full border-4 rounded-sm ${selectedLeadsTab === 1 ? 'border-primaryColor' : 'border-transparent'}`}
                onClick={() => setSelectedLeadsTab(1)}
              >
                <Card
                  title={t('deals')}
                  description={thousand((myLeadsHeader?.totalAssigned + myLeadsHeader?.totalRevisit + myLeadsHeader?.totalFailedCredit))}
                >
                  <p className="text-right text-xs"><strong>{t('waiting_signature')}:</strong> {thousand(myLeadsHeader.waitingSignatureKw / 1000)}kw ({thousand(myLeadsHeader.waitingSignatureCount)})</p>
                  <p className="text-right text-xs"><strong>{t('site_survey_completed')}:</strong> {thousand(myLeadsHeader.siteSurveyedKw / 1000)}kw ({thousand(myLeadsHeader.siteSurveyedCount)})</p>
                  <p className="text-right text-xs"><strong>{t('pending_install')}:</strong> {thousand(myLeadsHeader.pendingInstallKw / 1000)}kw ({thousand(myLeadsHeader.pendingInstallCount)})</p>
                </Card>
              </button>
            </Spin>
            <Spin spinning={myLeadsHeader?.loading}>
              <button
                aria-label={t('installs')}
                type="button"
                className={`col-span-1 w-full border-4 rounded-sm ${selectedLeadsTab === 2 ? 'border-primaryColor' : 'border-transparent'}`}
                onClick={() => setSelectedLeadsTab(2)}
              >
                <Card
                  title={t('installs')}
                  description={thousand((myLeadsHeader?.totalInstalled + myLeadsHeader?.totalActivated))}
                >
                  <p className="text-right text-xs"><strong>{t('installed')}:</strong> {thousand(myLeadsHeader.installedKw / 1000)}kw ({thousand(myLeadsHeader.installedCount)})</p>
                  <p className="text-right text-xs"><strong>{t('PTO')}:</strong> {thousand(myLeadsHeader.activatedKw / 1000)}kw ({thousand(myLeadsHeader.totalActivated)})</p>
                </Card>
              </button>
            </Spin>
            <Spin spinning={myLeadsHeader?.loading}>
              <button
                aria-label={t('lost')}
                type="button"
                className={`col-span-1 w-full border-4 rounded-sm ${selectedLeadsTab === 3 ? 'border-primaryColor' : 'border-transparent'}`}
                onClick={() => setSelectedLeadsTab(3)}
              >
                <Card
                  title={t('lost')}
                  description={thousand(myLeadsHeader?.totalLost)}
                  fakeChildren      
                />
              </button>
            </Spin>
          </div>
          <div className="grid gap-4 mt-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-5">
            {_.map(myLeadsHeader?.orgLeadsInfos, (org) => (
              <Spin spinning={myLeadsHeader?.loading}>
                <button
                  aria-label={t('assigned')}
                  type="button"
                  className="col-span-1 w-full"
                  onClick={() => history.push(`/admin/leads/${org.referralCode}`)}
                >
                  <Card
                    title={org?.name}
                    description={thousand(org?.totalLeads)}
                  />
                </button>
              </Spin>
            ))}
          </div>
          {selectedLeadsTab === -1 && (
            <LeadsSearchTable useGlobal searchType={0} className="mt-2" />
          )}
          {selectedLeadsTab === 0 && (
            <LeadsPending useGlobal className="mt-2" />
          )}
          {selectedLeadsTab === 1 && (
            <LeadsOpen useGlobal className="mt-2" />
          )}
          {selectedLeadsTab === 2 && (
            <LeadsClosed useGlobal className="mt-2" />
          )}
          {selectedLeadsTab === 3 && (
            <LeadsLost useGlobal className="mt-2" />
          )}
        </TabPane>
        <TabPane key="2" tab={t('closers')}>
          <OrgClosersSummaryTable useGlobal />
        </TabPane>
        <TabPane key="3" tab={t('setters')}>
          <OrgSettersSummaryTable useGlobal />
        </TabPane>
        <TabPane key="4" tab={t('payouts')}>
          <PayoutsSummary useGlobal />
        </TabPane>
        <TabPane key="5" tab={t('installs_map')}>
          <InstallMaps useGlobal />
        </TabPane>
      </Tabs>
    </MainPage>
  );
}

export default AdminLeads;
